
import {useCallback, useEffect, useState} from 'react'
import {Button, ButtonGroup} from 'react-bootstrap'
import Modal from '../../Modal'
import {AudienceModel, CreateOrUpdateAudienceModel} from '../../../redux/inAppMessageTypes'
import AllUsersAudienceEditor from './AllUsersAudienceEditor'
import EmployerAudienceEditor from './EmployerAudienceEditor'
import useInAppMessageActions from "../../../hooks/useInAppMessageActions"
import {createUseStyles} from "react-jss";

interface Props {
  inAppMessageId: string
  initialValue: AudienceModel | undefined
  visible: boolean
  onCloseClick?: () => void
}

const createEmptyAudienceRequestModel = (type: CreateOrUpdateAudienceModel['type'] | undefined): CreateOrUpdateAudienceModel => {
  switch (type) {
    case 'allUsers':
      return {type: 'allUsers'}
    case 'employer':
      return {type: 'employer', employerIds: []}
    default:
      return {type: 'allUsers'}
  }
}

const AudienceEditorModal = ({inAppMessageId, initialValue, visible, onCloseClick}: Props) => {
  const styles = useStyles()
  const [requestModel, setRequestModel] = useState<CreateOrUpdateAudienceModel>({type: 'allUsers'})

  const {setAudience} = useInAppMessageActions()

  useEffect(() => {
    setRequestModel(createEmptyAudienceRequestModel(initialValue?.type))
  }, [initialValue]);

  const handleSave = () => {
    if (!!requestModel) {
      setAudience(inAppMessageId, requestModel)
    }

    onCloseClick && onCloseClick()
  }

  const handleCancel = () => {
    onCloseClick && onCloseClick()
  }

  const handleAudienceChange = useCallback((next: CreateOrUpdateAudienceModel) => {
    setRequestModel(next)
  }, [setRequestModel])


  const renderTypeSelector = () => {
    const types: { key: CreateOrUpdateAudienceModel['type'], label: string }[] = [
      {key: 'allUsers', label: 'All Users'},
      {key: 'employer', label: 'Specific Employers'}
    ]

    return (
      <div className={styles.typeSelector}>
        <ButtonGroup className={styles.buttonGroup}>
          {types.map(({key, label}) => (
            <Button
              key={key}
              variant={key === requestModel.type ? 'primary' : 'outline-primary'}
              onClick={() => {
                if (requestModel.type !== key) {
                  setRequestModel(createEmptyAudienceRequestModel(key))
                }
              }}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </div>
    )
  }

  return (
    <Modal
      size={"lg"}
      title={'Edit audience'}
      show={visible}
      okButtonTitle='Save'
      cancelButtonTitle='Cancel'
      onHide={handleCancel}
      onOkClick={handleSave}
      onCancelClick={handleCancel}
    >
      <div style={{minHeight: 500}}>
        {renderTypeSelector()}

        {requestModel.type === 'allUsers' && (
          <AllUsersAudienceEditor
            audience={initialValue?.type === 'allUsers' ? initialValue : undefined}
            onChange={handleAudienceChange}
          />
        )}

        {requestModel.type === 'employer' && (
          <EmployerAudienceEditor
            audience={initialValue?.type === 'employer' ? initialValue : undefined}
            onChange={handleAudienceChange}
          />
        )}
      </div>
    </Modal>
  )
}

const useStyles = createUseStyles({
  typeSelector: {
    marginBottom: 20
  },
  buttonGroup: {
    '& .btn-outline-primary': {
      color: '#ffffff !important',
      backgroundColor: '#6c757d !important',
      borderColor: '#6c757d !important',

      '&:hover': {
        color: '#ffffff !important',
        backgroundColor: '#5a6268 !important',
        borderColor: '#545b62 !important'
      },

      '&:focus': {
        color: '#ffffff !important',
        backgroundColor: '#5a6268 !important',
        borderColor: '#545b62 !important',
      },

      '&:active': {
        color: '#ffffff !important',
        backgroundColor: '#545b62 !important',
        borderColor: '#4e555b !important'
      }
    },
    '& .btn-primary': {
      borderColor: '#6c757d !important',

      '&:hover': {
        color: '#ffffff !important',
        backgroundColor: '#5a6268 !important',
        borderColor: '#545b62 !important'
      }
    }
  }
})

export default AudienceEditorModal
